'use client'

import { format } from 'date-fns'
import { CalendarIcon, Clock } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { Button } from './button'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

function generateTimeSlots() {
  const slots: { hours: number; minutes: number; label: string }[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const period = h >= 12 ? 'PM' : 'AM'
      const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h
      const label = `${displayHour}:${String(m).padStart(2, '0')} ${period}`
      slots.push({ hours: h, minutes: m, label })
    }
  }
  return slots
}

const TIME_SLOTS = generateTimeSlots()

function formatTime(date: Date): string {
  const h = date.getHours()
  const m = date.getMinutes()
  const period = h >= 12 ? 'PM' : 'AM'
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${displayHour}:${String(m).padStart(2, '0')} ${period}`
}

interface DateTimePickerProps {
  value?: Date
  onChange: (date: Date | undefined) => void
  label?: string
  required?: boolean
  disabled?: boolean
}

function DateTimePicker({ value, onChange, label, required, disabled }: DateTimePickerProps) {
  const [dateOpen, setDateOpen] = React.useState(false)
  const [timeOpen, setTimeOpen] = React.useState(false)
  const selectedHours = value?.getHours()
  const selectedMinutes = value?.getMinutes()

  const scrollCallbackRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) {
        return
      }
      const activeEl = node.querySelector("[data-active='true']")
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'center' })
      } else {
        const now = new Date()
        const nearestSlot = Math.round((now.getHours() * 60 + now.getMinutes()) / 15) * 15
        const h = Math.floor(nearestSlot / 60) % 24
        const m = nearestSlot % 60
        const el = node.querySelector(`[data-time='${h}:${m}']`)
        el?.scrollIntoView({ block: 'center' })
      }
    },
    [selectedHours, selectedMinutes],
  )

  function handleDateSelect(selectedDate: Date | undefined) {
    if (!selectedDate) {
      onChange(undefined)
      setDateOpen(false)
      return
    }

    if (value) {
      selectedDate.setHours(value.getHours(), value.getMinutes(), 0, 0)
    } else {
      selectedDate.setHours(9, 0, 0, 0)
    }

    onChange(selectedDate)
    setDateOpen(false)
  }

  function handleTimeSelect(hours: number, minutes: number) {
    const updated = value ? new Date(value) : new Date()
    updated.setHours(hours, minutes, 0, 0)
    onChange(updated)
    setTimeOpen(false)
  }

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="block text-sm font-medium text-foreground">
          {label} {required && '*'}
        </label>
      )}
      <div className="flex gap-2">
        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className={cn('flex-1 min-w-0 justify-start text-left font-normal', !value && 'text-muted-foreground')}>
              <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
              <span className="truncate">{value ? format(value, 'MMM d, yyyy') : 'Select date'}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="start">
            <Calendar mode="single" selected={value} defaultMonth={value} onSelect={handleDateSelect} />
          </PopoverContent>
        </Popover>

        <Popover open={timeOpen} onOpenChange={setTimeOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className={cn(
                'w-[120px] sm:w-[130px] justify-start text-left font-normal',
                !value && 'text-muted-foreground',
              )}>
              <Clock className="mr-2 h-4 w-4 shrink-0" />
              {value ? formatTime(value) : 'Time'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[160px] p-0" align="start">
            <div ref={scrollCallbackRef} className="max-h-[280px] overflow-y-auto overscroll-contain py-1">
              {TIME_SLOTS.map((slot) => {
                const isActive = selectedHours === slot.hours && selectedMinutes === slot.minutes
                return (
                  <button
                    key={`${slot.hours}:${slot.minutes}`}
                    type="button"
                    data-active={isActive}
                    data-time={`${slot.hours}:${slot.minutes}`}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm transition-colors',
                      isActive ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-accent',
                    )}
                    onClick={() => handleTimeSelect(slot.hours, slot.minutes)}>
                    {slot.label}
                  </button>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}

export { DateTimePicker, type DateTimePickerProps }
